import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Alert,
  Card,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetProductByIdQuery,
  useUpdateProductMutation,
} from "../slices/productApiSlice";
import { useGetCategoriesQuery } from "../slices/categoryApiSlice";

const CLOUDINARY_CLOUD_NAME = "dsoi84tie";
const CLOUDINARY_UPLOAD_PRESET = "karaikudi";

const EditProductScreen = () => {
  const { id: productId } = useParams();
  const navigate = useNavigate();

  // 🔹 Product fields
  const [name, setName] = useState("");
  const [Tamil, setTamil] = useState("");
  const [description, setDescription] = useState("");

  // Pricing
  const [price, setPrice] = useState("");
  const [shopPrice, setShopPrice] = useState("");
  const [cost, setCost] = useState("");
  const [discount, setDiscount] = useState("");

  // Inventory
  const [countInStock, setCountInStock] = useState("");
  const [reOrderLevel, setReOrderLevel] = useState("");
  const [expiryDate, setExpiryDate] = useState("");

  // Categorization
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [subcategoriesList, setSubcategoriesList] = useState([]);

  // GST (DENORMALIZED)
  const [hsn, setHsn] = useState("");
  const [gstRate, setGstRate] = useState("");
  const [cgst, setCgst] = useState("");
  const [sgst, setSgst] = useState("");
  const [igst, setIgst] = useState("");

  // Media & status
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isActive, setIsActive] = useState(true);

  // API
  const { data: product, isLoading, error } =
    useGetProductByIdQuery(productId);

  const [updateProduct, { isLoading: isUpdating }] =
    useUpdateProductMutation();

  const { data: categories } = useGetCategoriesQuery();

  /* ---------------- Populate Product ---------------- */
  useEffect(() => {
    if (!product) return;

    setName(product.name || "");
    setTamil(product.Tamil || "");
    setDescription(product.description || "");

    setPrice(product.price ?? "");
    setShopPrice(product.shopPrice ?? "");
    setCost(product.cost ?? "");
    setDiscount(product.discount ?? "");

    setCountInStock(product.countInStock ?? "");
    setReOrderLevel(product.reOrderLevel ?? "");
    setExpiryDate(product.expiryDate?.split("T")[0] || "");

    setCategory(product.category || "");
    setSubcategory(product.subcategory || "");

    setImages(product.images || []);
    setIsActive(product.isActive ?? true);

    // GST
    setHsn(product.hsn || "");
    setGstRate(product.gstRate ?? "");
    setCgst(product.cgst ?? "");
    setSgst(product.sgst ?? "");
    setIgst(product.igst ?? "");
  }, [product]);

  /* ---------------- Category → Subcategory ---------------- */
  useEffect(() => {
    if (!categories) return;
    const selected = categories.find((c) => c.name === category);
    setSubcategoriesList(selected?.subcategories || []);
  }, [category, categories]);

  /* ---------------- GST Auto Calculation ---------------- */
  const handleGstRateChange = (e) => {
    const value = e.target.value;
    setGstRate(value);

    const rate = Number(value);
    if (!isNaN(rate)) {
      setCgst(rate / 2);
      setSgst(rate / 2);
      setIgst(rate);
    } else {
      setCgst("");
      setSgst("");
      setIgst("");
    }
  };
const toNumber = (val) => {
  const num = Number(val);
  return isNaN(num) ? 0 : num;
};

  /* ---------------- Image Upload ---------------- */
  const uploadImagesHandler = async (e) => {
    const files = Array.from(e.target.files);
    setUploading(true);

    try {
      const uploaded = await Promise.all(
        files.map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

          const res = await fetch(
            `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
            { method: "POST", body: formData }
          );

          const data = await res.json();
          return data.secure_url;
        })
      );

      setImages((prev) => [...prev, ...uploaded]);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (url) =>
    setImages((prev) => prev.filter((img) => img !== url));

  /* ---------------- Submit ---------------- */
  const submitHandler = async (e) => {
  e.preventDefault();

  // 🔍 RAW INPUT VALUES
  console.log("RAW GST INPUTS", { gstRate, cgst, sgst, igst });

  // 🔢 NORMALIZE
  const rate = toNumber(gstRate);
  const cg = toNumber(cgst);
  const sg = toNumber(sgst);
  const ig = rate; // IGST = gstRate

  console.log("NORMALIZED GST VALUES", { gstRate: rate, cgst: cg, sgst: sg, igst: ig });

  const totalTax = cg + sg;

  console.log("GST TOTAL CHECK", { totalTax, gstRate: rate, isValid: totalTax === rate });

  // ✅ VALIDATION: ONLY CGST + SGST must equal GST RATE
  if (totalTax !== rate) {
    alert(
      `GST mismatch ❌
GST Rate: ${rate}%
CGST + SGST: ${totalTax}%`
    );
    return;
  }

  // 📦 FINAL PAYLOAD
  const payload = {
    name,
    description,
    price: Number(price),
    shopPrice: Number(shopPrice),
    cost: cost ? Number(cost) : undefined,
    discount: discount ? Number(discount) : 0,
    countInStock: Number(countInStock),
    reOrderLevel: Number(reOrderLevel),
    expiryDate: expiryDate || undefined,
    category,
    subcategory,
    images,
    isActive,
    hsn,
    gstRate: rate,
    cgst: cg,
    sgst: sg,
    igst: ig, // store GST RATE for IGST
  };

  console.log("📤 PAYLOAD SENT TO API", payload);

  try {
    await updateProduct({ id: productId, ...payload }).unwrap();
    alert("Product updated successfully");
    navigate(-1);
  } catch (err) {
    console.error("❌ UPDATE ERROR", err);
    alert(err?.data?.message || "Failed to update product");
  }
};


  /* ---------------- UI ---------------- */
  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <h3 className="text-center mb-4">Update Product</h3>

          {isLoading ? (
            <Spinner />
          ) : error ? (
            <Alert variant="danger">Failed to load product</Alert>
          ) : (
            <Form onSubmit={submitHandler}>
              {/* Identity */}
              <Form.Group className="mb-3">
                <Form.Label>Product Name *</Form.Label>
                <Form.Control
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Product Name (Tamil)</Form.Label>
                <Form.Control
                  value={Tamil}
                  onChange={(e) => setTamil(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Form.Group>

              {/* Pricing */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>MRP *</Form.Label>
                    <Form.Control
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Shop Price *</Form.Label>
                    <Form.Control
                      type="number"
                      value={shopPrice}
                      onChange={(e) => setShopPrice(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Cost / Expiry */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cost Price</Form.Label>
                    <Form.Control
                      type="number"
                      value={cost}
                      onChange={(e) => setCost(e.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Expiry Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Stock */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Stock *</Form.Label>
                    <Form.Control
                      type="number"
                      value={countInStock}
                      onChange={(e) => setCountInStock(e.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Reorder Level *</Form.Label>
                    <Form.Control
                      type="number"
                      value={reOrderLevel}
                      onChange={(e) => setReOrderLevel(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Category */}
              <Card className="mb-3">
                <Card.Header>Product Classification</Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Category *</Form.Label>
                    <Form.Select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      <option value="">Select</option>
                      {(categories || []).map((c) => (
                        <option key={c.name} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Subcategory *</Form.Label>
                    <Form.Select
                      value={subcategory}
                      disabled={!category}
                      onChange={(e) => setSubcategory(e.target.value)}
                    >
                      <option value="">Select</option>
                      {(subcategoriesList || []).map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Card.Body>
              </Card>

              {/* Discount */}
              <Form.Group className="mb-3">
                <Form.Label>Discount</Form.Label>
                <Form.Control
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                />
              </Form.Group>

              {/* GST */}
              <Card className="mb-3 border-success">
                <Card.Header className="bg-success text-white">
                  GST Details
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>HSN *</Form.Label>
                    <Form.Control
                      value={hsn}
                      onChange={(e) => setHsn(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>GST Rate % *</Form.Label>
                    <Form.Control
                      type="number"
                      value={gstRate}
                      onChange={handleGstRateChange}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Control
                        className="mb-2"
                        type="number"
                        placeholder="CGST %"
                        value={cgst}
                        onChange={(e) => setCgst(e.target.value)}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Control
                        className="mb-2"
                        type="number"
                        placeholder="SGST %"
                        value={sgst}
                        onChange={(e) => setSgst(e.target.value)}
                      />
                    </Col>
                  </Row>

                  <Form.Control
                    type="number"
                    placeholder="IGST %"
                    value={igst}
                    onChange={(e) => setIgst(e.target.value)}
                  />
                </Card.Body>
              </Card>

              {/* Images */}
              <Form.Group className="mb-3">
                <Form.Control type="file" multiple onChange={uploadImagesHandler} />
              </Form.Group>

              {images.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {images.map((img) => (
                    <div key={img} className="position-relative">
                      <img src={img} alt="" height={80} />
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => removeImage(img)}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <Form.Check
                type="switch"
                label="Active"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="mb-3"
              />

              <Button type="submit" className="w-100" disabled={isUpdating}>
                {isUpdating ? "Updating..." : "Update Product"}
              </Button>
            </Form>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default EditProductScreen;
